import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import store, { dispatch } from 'store'
import { T } from 'consts/translationKeys'
import { translate } from 'utils/translations'
import { downloadMedia } from 'utils'
import { SUB_SIDEBAR_KEYS } from 'consts/sidebar'
import { AI_GENERATE_TAB_KEY } from 'containers/SubSidebar/AiImageContent/consts'
import { IAIImageProject, IReferenceImage } from 'store/types/aiImage'
import { aiImageThunks } from 'store/features/aiImage/thunks'
import { aiLibImageSelectors } from 'store/features/aiLibImage/selectors'
import { aiLibImageThunks } from 'store/features/aiLibImage/thunks'
import { editorThunks } from 'store/features/editor/thunks'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { aiVideoThunks } from 'store/features/aiVideo/thunks'
import { useAlertPopupsContext } from 'context/AlertPopups'
import { useAddAiItemToTimeline } from 'hooks/useAddAiItemToTimeline'
import { convertAiImgToBaseMediaItem } from 'containers/SubSidebar/AiImageContent/HistoryTab/Content/Image/utils'
import { IFlyoutMenuItem } from 'common-components/ActionsFlyout/types'
import { IOverlayActionsData } from 'common-components/ActionsOverlay/types'
import DownloadIcon from 'assets/svgr-icons/Download.svg'
import PaletteIcon from 'assets/svgr-icons/Palette.svg'
import PlusIcon from 'assets/svgr-icons/Plus.svg'
import RegenerateIcon from 'assets/svgr-icons/Regenerate.svg'
import RemoveIcon from 'assets/svgr-icons/Remove.svg'
import StarAnimateIcon from 'assets/svgr-icons/Star_Animate.svg'

const MENU_ICON_SIZE = { width: 16, height: 16 }

const menuIcons = {
  add: <PlusIcon />,
  regenerate: <RegenerateIcon />,
  reference: <PaletteIcon />,
  animate: <StarAnimateIcon />,
  download: <DownloadIcon />,
  delete: <RemoveIcon />
} as const

interface IUseAiLibImageActionsProps {
  imageProject: IAIImageProject
  onClosePreview?: () => void
}

interface IUseAiLibImageActionsReturn extends IOverlayActionsData {
  handleAddToTimeline: () => void
  handleCreateSimilar: () => void
  handleUseAsReference: () => void
  handleAnimate: () => void
  handleDownload: () => void
  isAiGenerated: boolean
}

export const useAiLibImageActions = ({
  imageProject,
  onClosePreview
}: IUseAiLibImageActionsProps): IUseAiLibImageActionsReturn => {
  const addItemToTimeline = useAddAiItemToTimeline()
  const isAiGenerated = !!imageProject.prompt
  const { setAlertPopupsData } = useAlertPopupsContext()
  const historyList = useSelector(aiLibImageSelectors.historyList)
  const historyTotalCount = useSelector(aiLibImageSelectors.historyTotalCount)

  const image = imageProject.images[0]
  const filePath = image?.filePath || ''
  const imageId = image?.id

  const handleAddToTimeline = useCallback(() => {
    addItemToTimeline(convertAiImgToBaseMediaItem(imageProject))
    onClosePreview?.()
  }, [addItemToTimeline, imageProject, onClosePreview])

  const switchToAiImageGenerate = useCallback(() => {
    dispatch(editorThunks.setActiveSidebarTab(SUB_SIDEBAR_KEYS.AI_IMAGE))
  }, [])

  const handleCreateSimilar = useCallback(() => {
    if (!imageId || !image) return
    onClosePreview?.()

    const mediaItems = projectDataSelectors.mediaItems(store.getState())
    const matchingItem = mediaItems.find(
      item => String(item.assetId) === String(imageId)
    )

    if (matchingItem) {
      dispatch(
        editorThunks.setActiveTimelineItems([
          { id: matchingItem.id, type: matchingItem.type }
        ])
      )
    } else {
      dispatch(editorThunks.setActiveTimelineItems([]))
    }

    switchToAiImageGenerate()
    dispatch(aiImageThunks.resetVersionHistory())
    dispatch(aiImageThunks.updateGenerateForm({ id: imageProject.id }))
    dispatch(aiImageThunks.setGenerationParamsByAssetId(Number(imageId)))
    const referenceImage: IReferenceImage = {
      id: imageProject.id,
      url: filePath,
      thumbnailUrl: image.thumbnailPath || filePath,
      width: imageProject.width,
      height: imageProject.height
    }
    dispatch(aiImageThunks.replaceReferenceImages([referenceImage]))
    dispatch(aiImageThunks.setTargetMediaItemId(null))
    dispatch(aiImageThunks.setShowVersionHistoryTab(true))
    dispatch(aiImageThunks.setActiveTab(AI_GENERATE_TAB_KEY))
  }, [
    imageId,
    image,
    onClosePreview,
    switchToAiImageGenerate,
    imageProject,
    filePath
  ])

  const handleUseAsReference = useCallback(() => {
    if (!filePath || !image) return

    onClosePreview?.()
    switchToAiImageGenerate()
    dispatch(aiImageThunks.resetVersionHistory())
    dispatch(aiImageThunks.updateGenerateForm({ id: imageProject.id }))
    const { id, width, height } = imageProject
    const referenceImage: IReferenceImage = {
      id,
      url: filePath,
      thumbnailUrl: image.thumbnailPath || filePath,
      width,
      height
    }
    dispatch(aiImageThunks.replaceReferenceImages([referenceImage]))
    dispatch(aiImageThunks.setTargetMediaItemId(null))
    dispatch(aiImageThunks.setShowVersionHistoryTab(true))
    dispatch(aiImageThunks.setActiveTab(AI_GENERATE_TAB_KEY))
  }, [filePath, image, imageProject, onClosePreview, switchToAiImageGenerate])

  const handleAnimate = useCallback(() => {
    if (!imageId) return
    dispatch(
      aiVideoThunks.animateAiImage({ assetId: imageId, isAiImage: true })
    )
  }, [imageId])

  const handleDownload = useCallback(async () => {
    if (!filePath || !imageId) return
    await downloadMedia({
      filePath,
      fileName: `ai-image-${imageId}`
    })
  }, [filePath, imageId])

  const handleDelete = useCallback(async () => {
    await dispatch(aiImageThunks.deleteProjectById(imageProject.id))
    dispatch(
      aiLibImageThunks.setHistoryList(
        historyList.filter(item => item.id !== imageProject.id)
      )
    )
    dispatch(
      aiLibImageThunks.setHistoryTotalCount(Math.max(0, historyTotalCount - 1))
    )
  }, [historyList, historyTotalCount, imageProject.id])

  const handleDeleteWarning = useCallback(() => {
    setAlertPopupsData({
      warningPopup: {
        title: translate(T.DELETE_AI_IMAGE_CONFIRMATION_TITLE),
        description: translate(T.DELETE_AI_IMAGE_CONFIRMATION_DESCRIPTION),
        buttons: {
          cancel: {
            label: translate(T.CANCEL_LABEL)
          },
          confirm: {
            label: translate(T.DELETE_LABEL),
            onClick: handleDelete
          }
        }
      }
    })
  }, [handleDelete, setAlertPopupsData])

  const secondaryActions = useMemo<IFlyoutMenuItem[]>(
    () => [
      ...(isAiGenerated
        ? [
            {
              key: T.CREATE_SIMILAR,
              title: translate(T.CREATE_SIMILAR),
              icon: menuIcons.regenerate,
              onClick: handleCreateSimilar,
              iconSize: MENU_ICON_SIZE
            }
          ]
        : []),
      {
        key: T.USE_AS_REFERENCE,
        title: translate(T.USE_AS_REFERENCE),
        icon: menuIcons.reference,
        onClick: handleUseAsReference,
        iconSize: MENU_ICON_SIZE
      },
      {
        key: T.ANIMATE,
        title: translate(T.CUSTOM_ANIMATE),
        icon: menuIcons.animate,
        onClick: handleAnimate,
        iconSize: MENU_ICON_SIZE
      },
      {
        key: T.DOWNLOAD,
        title: translate(T.DOWNLOAD),
        icon: menuIcons.download,
        onClick: handleDownload,
        iconSize: MENU_ICON_SIZE
      },
      {
        key: T.DELETE_LABEL,
        title: translate(T.DELETE_LABEL),
        icon: menuIcons.delete,
        onClick: handleDeleteWarning,
        iconSize: MENU_ICON_SIZE
      }
    ],
    [
      isAiGenerated,
      handleCreateSimilar,
      handleUseAsReference,
      handleAnimate,
      handleDownload,
      handleDeleteWarning
    ]
  )

  return {
    secondaryActions:
      secondaryActions as IUseAiLibImageActionsReturn['secondaryActions'],
    handleAddToTimeline,
    handleCreateSimilar,
    handleUseAsReference,
    handleAnimate,
    handleDownload,
    isAiGenerated
  }
}
